// Ultimate Card Game (UCG) - Campaign Cutscene Player
//
// Minimal, data-driven playback for the in-engine scripted cutscenes
// described in docs/game-design-singleplayer-kampagne.md Kapitel 6/9.3.
// Deliberately reuses existing machinery instead of building a new one:
//
// - Camera: drives a synthetic plain-object tracking target (same trick as
//   Game._startCameraIntro() in js/main.js) through js/ui/viewport.js's
//   already-existing startTrackingUnit()/updateUnitTracking() lerp - this
//   player never touches viewport.camera directly.
// - Actor movement: simple A-to-B-over-duration lerp along a path, the same
//   pattern as ParticleEngine.addShockwave() (js/engine/particles.js).
// - Actor visuals: a `unitActor` step (see below) makes an actor a real,
//   fully-sprited Unit; a plain `actor` step (no prior `unitActor` for that
//   id) keeps the original placeholder-circle rendering in renderActors()
//   for cast members that don't need real game sprites (e.g. the animal).
//
// A timeline is a plain object: { steps: [...] }, each step one of:
//   { type: 'camera', to: {x, y, zoom?}, duration, at }
//   { type: 'cameraFollow', actorId, zoom?, duration, at }
//   { type: 'actor', actorId, path: [{x,y}, ...], anim?, duration, at }
//   { type: 'unitActor', actorId, cardName, team, x, y, isParachuting?, parachuteDuration?, at }
//   { type: 'actorHide', actorId, at }
//   { type: 'sfx', sound, at }
//   { type: 'hud', visible, at }
//   { type: 'particles', actorId, effect: 'shockwave', radius?, color?, duration?, at }
//
// `unitActor` (docs/singleplayer implementation soft-foraging-flamingo.md
// Phase 3) spawns a REAL battlefield.spawnRaw() Unit for this actor instead
// of the plain-object placeholder `_actor()` creates - isCinematic:true
// (js/engine/unit.js) keeps it out of targeting/attack/marching while
// subsequent `actor`/`cameraFollow` steps drive its x/y directly, exactly
// like the plain-object case. Being a real, battlefield-registered Unit
// means the normal per-frame renderer.render() unit loop already draws its
// full sprite - renderActors() below deliberately does NOT re-draw it
// (would double-composite the sprite during its spawn fade-in).
// `at` is seconds from the timeline's own start; `duration` (camera/actor/
// cameraFollow) is how long that step's transition/hold takes.
//
// `cameraFollow` tracks a moving actor for its whole duration instead of
// lerping toward one fixed point - the tight zoom this affords is what
// keeps the marines out of frame until the walk actually arrives at them
// ("man darf nicht so weit blicken dürfen", docs/game-design-singleplayer-
// kampagne.md). List the matching `actor` step(s) for the same actorId
// BEFORE the `cameraFollow` step in the `steps` array: both update once
// per frame in array order, so the actor's position for this frame must
// already be fresh by the time cameraFollow reads it.

import { Unit } from './unit.js';
import { getCardByName } from '../data/cards.js';

function lerp(a, b, t) {
  return a + (b - a) * t;
}

export class CutscenePlayer {
  constructor(game) {
    this.game = game;
    this.active = false;
    this.steps = [];
    this.elapsed = 0;
    this.actors = new Map();
    this.cameraTarget = null;
    this.onComplete = null;
  }

  // Starts a timeline. `onComplete` fires once every step has finished
  // (camera/actor steps run their full duration; sfx/hud fire once and
  // never hold up completion on their own).
  play(timeline, onComplete = null) {
    this.steps = (timeline.steps || []).map(s => ({ ...s, _started: false, _fired: false }));
    this.elapsed = 0;
    this.actors.clear();
    this.onComplete = onComplete;
    this.active = true;

    const worldW = this.game.battlefield?.width || 1600;
    const worldH = this.game.battlefield?.height || 540;
    this.cameraTarget = { x: worldW / 2, y: worldH / 2, isDead: false, isAir: false };
    this.game.viewport?.startTrackingUnit?.(this.cameraTarget);
  }

  _actor(id) {
    if (!this.actors.has(id)) {
      this.actors.set(id, { x: 0, y: 0, anim: null });
    }
    return this.actors.get(id);
  }

  update(dt) {
    if (!this.active) return;
    this.elapsed += dt;

    let stillRunning = false;
    for (const step of this.steps) {
      // Once a camera/actor/cameraFollow step has reached its final frame
      // it's marked _done and skipped from here on - without this, every
      // step in `steps` gets reprocessed every single frame for the rest
      // of the timeline (harmless idempotent re-lerping for camera/actor
      // on their own, but it silently re-creates any actor an `actorHide`
      // step further down already deleted, the instant the next frame
      // reprocesses that actor's now-"finished" `actor` step again).
      if (step._done) continue;

      const start = step.at || 0;
      if (this.elapsed < start) {
        stillRunning = true;
        continue;
      }

      const duration = step.duration || 0;
      const progress = duration > 0 ? Math.min(1, (this.elapsed - start) / duration) : 1;

      switch (step.type) {
        case 'camera': {
          if (!step._started) {
            step._fromX = this.cameraTarget.x;
            step._fromY = this.cameraTarget.y;
            step._fromZoom = this.cameraTarget.__introZoomMult ?? 1;
            step._started = true;
          }
          this.cameraTarget.x = lerp(step._fromX, step.to.x, progress);
          this.cameraTarget.y = lerp(step._fromY, step.to.y, progress);
          if (step.to.zoom != null) {
            this.cameraTarget.__introZoomMult = lerp(step._fromZoom, step.to.zoom, progress);
          }
          break;
        }
        case 'actor': {
          const actor = this._actor(step.actorId);
          actor.anim = step.anim || actor.anim;
          const path = step.path || [];
          if (path.length === 1) {
            actor.x = path[0].x;
            actor.y = path[0].y;
          } else if (path.length >= 2) {
            const segProgress = progress * (path.length - 1);
            const idx = Math.min(path.length - 2, Math.floor(segProgress));
            const segT = segProgress - idx;
            actor.x = lerp(path[idx].x, path[idx + 1].x, segT);
            actor.y = lerp(path[idx].y, path[idx + 1].y, segT);
          }
          // Real parachuting Unit actors need their own fall-height decay
          // driven here too: Unit.update() (js/engine/unit.js) is what
          // normally ages parachuteAltitude down to 0 as parachuteTimer
          // expires, but Unit.update() never runs at all while a cutscene
          // is active (see Game.gameLoop()) - this step's own `progress`
          // (0..1 over `duration`) is already exactly the fall's timeline,
          // so it mirrors that same 36px-to-0 easing directly instead.
          if (actor instanceof Unit && actor.isParachuting) {
            actor.parachuteAltitude = Math.max(0, (1 - progress) * 36);
            if (progress >= 1) {
              actor.isParachuting = false;
              actor.parachuteAltitude = 0;
            }
          }
          break;
        }
        case 'unitActor': {
          // One-shot spawn, like `sfx`/`hud`/`actorHide` below - guarded by
          // its own `_started` flag rather than `_done` (this step type
          // never participates in the camera/actor/cameraFollow duration
          // bookkeeping at the bottom of this loop).
          if (!step._started) {
            step._started = true;
            const baseCard = getCardByName(step.cardName);
            if (baseCard) {
              // Per-encounter stat overrides (docs/singleplayer
              // implementation soft-foraging-flamingo.md) - same "clone,
              // never mutate the shared card" pattern as Black Marine's
              // enemy hpOverride (js/main.js's startCampaignMission(),
              // docs/game-design-singleplayer-kampagne.md Kapitel 5), just
              // driven from the cutscene step instead of mission.enemies
              // since this is the PLAYER's own unit, not a pre-placed foe.
              const card = step.cardOverrides ? { ...baseCard, ...step.cardOverrides } : baseCard;
              const spawnOptions = { isCinematic: true };
              if (step.isParachuting) {
                const duration = step.parachuteDuration || 2.0;
                spawnOptions.isParachuting = true;
                spawnOptions.parachuteDuration = duration;
                spawnOptions.parachuteTimer = duration;
                spawnOptions.parachuteAltitude = 36;
              }
              const unit = this.game.battlefield.spawnRaw(card, step.team, step.x, step.y, spawnOptions);
              this.actors.set(step.actorId, unit);
            } else {
              console.error(`[UCG] Cutscene unitActor references unknown card: ${step.cardName}`);
            }
          }
          break;
        }
        case 'cameraFollow': {
          const actor = this.actors.get(step.actorId);
          if (actor) {
            if (!step._started) {
              step._fromZoom = this.cameraTarget.__introZoomMult ?? 1;
              step._started = true;
            }
            this.cameraTarget.x = actor.x;
            this.cameraTarget.y = actor.y;
            if (step.zoom != null) {
              // Quick ease into the follow zoom (0.5s), then hold - a slow
              // multi-second lerp here would mean the camera is still
              // zooming out to catch up with the walk for the whole first
              // stretch, working against the "stay tight" point of this
              // step type.
              const zoomInDuration = Math.min(duration, 0.5) || 0.5;
              const zoomProgress = Math.min(1, (this.elapsed - start) / zoomInDuration);
              this.cameraTarget.__introZoomMult = lerp(step._fromZoom, step.zoom, zoomProgress);
            }
          }
          break;
        }
        case 'hud': {
          if (!step._fired) {
            this.game.hud?.setVisible?.(!!step.visible);
            step._fired = true;
          }
          break;
        }
        case 'actorHide': {
          // Removes a finished actor (e.g. the heli after it's done
          // flying in) so its last position doesn't linger as a ghost
          // marker in renderActors() for the rest of the timeline.
          if (!step._fired) {
            const actor = this.actors.get(step.actorId);
            // Real Unit actors (from `unitActor`) are also spliced straight
            // out of battlefield.units/unitMap here, not just out of this
            // Map: while a cutscene is active, Game.gameLoop() never calls
            // Battlefield.update() (that's what holds combat off), so the
            // usual isDead cleanup in Battlefield.update() never runs to
            // remove it - without this, the hidden unit's sprite would
            // keep rendering via the normal per-frame battlefield draw.
            if (actor instanceof Unit) {
              actor.isDead = true;
              const bf = this.game.battlefield;
              const idx = bf.units.indexOf(actor);
              if (idx !== -1) bf.units.splice(idx, 1);
              bf.unitMap.delete(actor.id);
            }
            this.actors.delete(step.actorId);
            step._fired = true;
          }
          break;
        }
        case 'sfx': {
          if (!step._fired) {
            // Platzhalter: kein Sound-Asset für diese Timeline hinterlegt,
            // siehe docs/game-design-singleplayer-kampagne.md Kapitel 9.3.
            step._fired = true;
          }
          break;
        }
        case 'particles': {
          // One-shot particle burst at an actor's current position (docs/
          // singleplayer implementation soft-foraging-flamingo.md) - reuses
          // the existing ParticleEngine effect methods (js/engine/
          // particles.js) rather than a new bespoke cutscene-only effect.
          if (!step._fired) {
            const actor = this.actors.get(step.actorId);
            if (actor && step.effect === 'shockwave') {
              this.game.particles?.addShockwave?.(actor.x, actor.y, step.radius || 40, step.color || '#00f0ff', step.duration || 0.25);
            }
            step._fired = true;
          }
          break;
        }
      }

      if (step.type === 'camera' || step.type === 'actor' || step.type === 'cameraFollow') {
        if (progress < 1) {
          stillRunning = true;
        } else {
          step._done = true;
        }
      }
    }

    if (!stillRunning) {
      this.finish();
    }
  }

  finish() {
    this.active = false;
    // Deliberately does NOT call stopTrackingUnit() here: that would ease
    // the camera straight back to the generic whole-map default framing
    // the instant the cutscene ends, undoing whatever shot it just
    // finished on (e.g. Mission 1's arrival at the marines) before the
    // player has even acted. Leaving viewport.trackedUnit pointing at this
    // now-frozen cameraTarget just holds the last frame in place - a real
    // startTrackingUnit() call later (e.g. Game._autoTrackCampaignPlayerUnit()
    // once the player plays a card) cleanly takes over from it.
    const cb = this.onComplete;
    this.onComplete = null;
    if (cb) cb();
  }

  // Called from Game.render(), after the normal battlefield draw, so
  // placeholder actor markers appear on top of the field.
  renderActors(ctx, viewport) {
    if (!this.active || !ctx || !viewport) return;
    for (const actor of this.actors.values()) {
      // Real Unit actors (`unitActor` step) are registered in
      // battlefield.units and already drawn every frame by the normal
      // renderer.render() unit loop - drawing them again here would
      // double-composite the sprite (visible during its spawn fade-in).
      // Only the plain-object placeholder actors (e.g. the animal) still
      // need the circle marker below.
      if (actor instanceof Unit) continue;
      const screen = viewport.worldToScreen(actor.x, actor.y);
      ctx.save();
      ctx.fillStyle = '#f8fafc';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }
}
